// The financial brief's arithmetic (Marketing Hub row 2.7a; plan section
// 5.9; build spec modal 22). What an effort should earn and what it costs,
// conservatively, with every number's source carried beside it.
//
// CONSUMER: shared (shared/db/financial-briefs.ts, the Today route, the
// brief modal, tests).
//
// THE FIVE ROWS (plan 5.9). Reached, then Opportunities, then Processing,
// then Closed, then Income. Each step's rate is the conservative end of its
// source: the benchmark row's `low` until the OS has its own cohort
// (switchRule below). Income is what one outcome pays Stewardship, first
// year and lifetime (always the pair), after the advisor's share.
//
// NEVER PRICED: effort. Calls, emails, notes and meetings are counted and
// printed beside the money; they are never a term in any subtraction and
// never turned into hours or dollars (Grant, 2026-09-25 and 2026-09-26).
// No function here takes a time or a rate per hour, on purpose.
//
// TWO SCALES. An audience (a goal, a campaign) is counted step by step. One
// household (a Today row) cannot be: 1 x 10% x 22% x 95% rounds to nothing.
// So a household's funnel is its CHANCE of closing (the rates multiplied),
// and its income is that chance times one outcome, beside what one outcome
// pays if it does close.
//
// ROUNDING (the approved drawing, screen 8 and modal 22). Each count is the
// previous ROUNDED count times the step's rate, rounded to the nearest
// whole family: 138 x 5% = 6.9, so 7; 7 x 22% = 1.54, so 2; 2 x 95% = 1.9,
// so 2. DECISION: nearest, not down. Rounding every step down would make
// most small efforts read as zero closed, which flatters nothing but tells
// the reader nothing either; the conservatism lives in the rates, which are
// already the low end of every source.
//
// Hard rule: PURE.

export const FUNNEL_STEPS = ["reach_to_opportunity", "opportunity_to_processing", "processing_to_closed"] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

export const FUNNEL_LABEL: Record<FunnelStep, string> = {
  reach_to_opportunity: "Opportunities",
  opportunity_to_processing: "Processing",
  processing_to_closed: "Closed",
};

export type EvidenceLabel = "established" | "directional" | "experimental";

export const EVIDENCE_WORD: Record<EvidenceLabel, string> = {
  established: "Established",
  directional: "Directional",
  experimental: "Experimental",
};

/** One step's rate and where it came from. */
export interface StepRate {
  step: FunnelStep;
  /** A fraction: 0.22 is 22%. */
  rate: number;
  /** The rate's upper end when the source states one; null otherwise. */
  high: number | null;
  /** "benchmark", "cohort" or "blend" (switchRule). */
  from: RateFrom;
  /** Printed beside the rate. Null means ASSUMED: no source exists. */
  source: string | null;
  /** The OS's own outcomes behind a cohort or blend rate; null for a benchmark. */
  sample: number | null;
}

export type RateFrom = "benchmark" | "cohort" | "blend";

// ─── The switch from benchmark to the OS's own numbers ────────────────

/** Plan 5.9: 30 or more outcomes across at least two quarters. */
export const COHORT_ESTABLISHED_OUTCOMES = 30;
export const COHORT_ESTABLISHED_QUARTERS = 2;
/** Plan 5.9: 10 to 29 blends cohort and benchmark half and half. */
export const COHORT_BLEND_OUTCOMES = 10;

export interface Cohort {
  outcomes: number;
  quarters: number;
  /** The cohort's 25th-percentile rate for this step. */
  p25: number;
}

/**
 * Which rate a step uses (plan 5.9, "The switch from benchmark to the OS's
 * own data"). With 30 or more outcomes across at least two quarters, the
 * OS's own 25th percentile; with 10 to 29, the cohort and the benchmark
 * half and half; under 10 (or no cohort), the benchmark's conservative end.
 * The result always says which it used and on how many outcomes.
 */
export function switchRule(benchmarkLow: number, cohort: Cohort | null): { rate: number; from: RateFrom; sample: number | null } {
  if (cohort === null || cohort.outcomes < COHORT_BLEND_OUTCOMES) return { rate: benchmarkLow, from: "benchmark", sample: cohort?.outcomes ?? null };
  if (cohort.outcomes >= COHORT_ESTABLISHED_OUTCOMES && cohort.quarters >= COHORT_ESTABLISHED_QUARTERS) {
    return { rate: cohort.p25, from: "cohort", sample: cohort.outcomes };
  }
  return { rate: (cohort.p25 + benchmarkLow) / 2, from: "blend", sample: cohort.outcomes };
}

/** Channels whose benchmarks have strong support (plan 5.9): existing-client
 *  email or call, a referral, direct mail. Paid channels never qualify. */
export const STRONG_BENCHMARK_CHANNELS: ReadonlySet<string> = new Set(["advisor_email_or_call", "referral", "direct_mail", "book"]);

/**
 * The evidence label (plan 5.9). Established: every step from the OS's own
 * cohort. Experimental: any step assumed (no source), or a paid channel on
 * benchmarks. Directional: everything else (a strong benchmark, or a thin
 * cohort blended with one).
 */
export function evidenceFor(steps: readonly StepRate[], channel: string | null): EvidenceLabel {
  if (steps.length === 0) return "experimental";
  if (steps.some((s) => s.source === null && s.from === "benchmark")) return "experimental";
  if (steps.every((s) => s.from === "cohort")) return "established";
  if (steps.some((s) => s.from === "benchmark") && (channel === null || !STRONG_BENCHMARK_CHANNELS.has(channel))) return "experimental";
  return "directional";
}

// ─── The funnel ───────────────────────────────────────────────────────

export interface FunnelBand {
  label: "Reached" | (typeof FUNNEL_LABEL)[FunnelStep];
  count: number;
  /** The rate that produced this band from the one before; null for Reached. */
  rate: number | null;
  source: string | null;
  from: RateFrom | null;
  sample: number | null;
}

/** An audience's funnel, counted step by step with the drawing's rounding. */
export function audienceFunnel(reached: number, steps: readonly StepRate[]): FunnelBand[] {
  const bands: FunnelBand[] = [{ label: "Reached", count: Math.max(0, Math.floor(reached)), rate: null, source: null, from: null, sample: null }];
  let count = bands[0]!.count;
  for (const s of steps) {
    count = Math.round(count * s.rate);
    bands.push({ label: FUNNEL_LABEL[s.step], count, rate: s.rate, source: s.source, from: s.from, sample: s.sample });
  }
  return bands;
}

/** One household's chance of closing: every step's rate multiplied. */
export function chanceOfClosing(steps: readonly StepRate[]): number {
  return steps.reduce((p, s) => p * s.rate, 1);
}

// ─── Income per outcome ───────────────────────────────────────────────

export interface IncomeRow {
  /** Dollars to Stewardship for one outcome in year one, before the advisor's share. */
  incomeY1: number;
  /** Recurring dollars each later year; null when the product pays once. */
  renewalPerYear: number | null;
  /** The share still with us each later year (0.85 is 85%). */
  annualRetention: number | null;
  /** How many years the income can last, counting year one. */
  expectedYears: number | null;
}

/**
 * Lifetime income for one outcome (plan 5.9): year one, plus each later
 * year's renewal times the share still with us that year, discounted. A
 * product that pays once (mortgage, realty, an estate plan) is year one.
 */
export function lifetimeIncome(row: IncomeRow, discount: number): number {
  const years = Math.max(1, Math.round(row.expectedYears ?? 1));
  if (row.renewalPerYear === null || row.annualRetention === null || years === 1) return row.incomeY1;
  let total = row.incomeY1;
  for (let k = 1; k < years; k++) {
    total += (row.renewalPerYear * row.annualRetention ** k) / (1 + discount) ** k;
  }
  return total;
}

/** The advisor's share taken out (basis points of the advisor's split);
 *  null leaves the amount as it is (no split on file). */
export function afterAdvisorShare(amount: number, advisorShareBps: number | null): number {
  if (advisorShareBps === null) return amount;
  const share = Math.min(10_000, Math.max(0, advisorShareBps));
  return amount * (1 - share / 10_000);
}

// ─── The verdict ──────────────────────────────────────────────────────

export interface CashItem {
  label: string;
  dollars: number;
  /** How it was counted ("1 drafted email x $0.011"). */
  basis: string;
}

/** Effort in plain counts. Never priced, never hours. */
export interface EffortCounts {
  calls: number;
  personal_emails: number;
  automatic_questions: number;
  notes: number;
  meetings: number;
}

export const NO_EFFORT: EffortCounts = { calls: 0, personal_emails: 0, automatic_questions: 0, notes: 0, meetings: 0 };

export interface Verdict {
  /** Closed outcomes expected: a count for an audience, a chance for one household. */
  expectedClosed: number;
  perOutcomeY1: number;
  perOutcomeLifetime: number;
  incomeY1: number;
  incomeLifetime: number;
  cashCost: number;
  netY1: number;
  netLifetime: number;
  /** Months of first-year income to cover the cash; null when it never does. */
  paybackMonths: number | null;
  /** Cash per closed outcome; null when nothing is expected to close. */
  costPerOutcome: number | null;
}

export function verdict(input: { expectedClosed: number; perOutcomeY1: number; perOutcomeLifetime: number; cashItems: readonly CashItem[] }): Verdict {
  const cashCost = input.cashItems.reduce((n, c) => n + c.dollars, 0);
  const incomeY1 = input.expectedClosed * input.perOutcomeY1;
  const incomeLifetime = input.expectedClosed * input.perOutcomeLifetime;
  let paybackMonths: number | null;
  if (cashCost <= 0) paybackMonths = 0;
  else if (incomeY1 <= 0) paybackMonths = null;
  else paybackMonths = Math.max(1, Math.ceil(cashCost / (incomeY1 / 12)));
  return {
    expectedClosed: input.expectedClosed,
    perOutcomeY1: input.perOutcomeY1,
    perOutcomeLifetime: input.perOutcomeLifetime,
    incomeY1,
    incomeLifetime,
    cashCost,
    netY1: incomeY1 - cashCost,
    netLifetime: incomeLifetime - cashCost,
    paybackMonths,
    costPerOutcome: input.expectedClosed > 0 ? cashCost / input.expectedClosed : null,
  };
}

// ─── Words ────────────────────────────────────────────────────────────

/** "$1,028", "-$160", "$0.01" (cents only under a dollar). */
export function dollars(n: number): string {
  const abs = Math.abs(n);
  const body = abs > 0 && abs < 1 ? `$${abs.toFixed(2)}` : `$${Math.round(abs).toLocaleString("en-US")}`;
  return n < 0 ? `-${body}` : body;
}

/** "+$1,028" or "-$160": a net always carries its sign. */
export function signedDollars(n: number): string {
  return n >= 0 ? `+${dollars(n)}` : dollars(n);
}

/** "22%", "2.5%", "0.4%". */
export function percent(rate: number): string {
  const p = rate * 100;
  return `${p >= 10 || Number.isInteger(p) ? Math.round(p) : Math.round(p * 10) / 10}%`;
}

/** "1 personal email · 1 call": the counts that are not zero, never hours. */
export function effortLine(e: EffortCounts): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${n.toLocaleString("en-US")} ${n === 1 ? one : many}`);
  };
  add(e.personal_emails, "personal email", "personal emails");
  add(e.automatic_questions, "question, automatic", "questions, automatic");
  add(e.calls, "call", "calls");
  add(e.notes, "note", "notes");
  add(e.meetings, "meeting", "meetings");
  return parts.length > 0 ? parts.join(" · ") : "No effort counted";
}

/** "pays back in month 1", "does not pay back on first-year income". */
export function paybackLine(months: number | null): string {
  if (months === null) return "does not pay back on first-year income";
  if (months === 0) return "no cash to pay back";
  return `pays back in month ${months}`;
}
